import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLoading } from '../../../../../state-management/contexts/LoadingContext'
import { goBack, goToPortfolioDashboard, lightImpactVibrate } from '../../../../../utils/appBridge'
import { fetchSipPlansOverview, updateIdealSipAmount } from '../api/repository'
import type { SipPlansOverviewResponse, UpdateIdealSipAmountRequest } from '../api/overview.types'
import { SipPlansPlanType } from '../api/common.types'
import { SIP_PLANS_API_RETRY_COUNT, SIP_SUMMARY_QUERY_KEY } from '../api/queryOptions'
import { SipPlansContext, type SipPlansContextValue, type SipPlansQueryState } from './sipPlansContext'
import { deriveSipPlansView } from './deriveSipPlansView'
import { useSipPlanCategoryExpansionState } from './usePlanCategoryBucketExpansion'
import { SipActionFilter, SipPlanMode } from '../api/types'

export type { SipPlansQueryState } from './sipPlansContext'
export { useSipPlansContext } from './sipPlansContext'

const SIP_EDIT_IDEAL_MIN_LOADING_MS = 250

function clearIdealSipEditLoadingTimeout(
  timeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
) {
  if (timeoutRef.current != null) {
    clearTimeout(timeoutRef.current)
    timeoutRef.current = null
  }
}

export function isSipPlansHeaderLoading(
  planMode: SipPlanMode,
  hasPlansOverview: boolean,
  plansQuery: SipPlansQueryState
): boolean {
  if (planMode !== SipPlanMode.YOUR_PLAN) {
    return false
  }
  if (plansQuery.isError && plansQuery.isFetching) {
    return true
  }
  return plansQuery.isLoading && !hasPlansOverview
}

export function isSipPlansLoading(planMode: SipPlanMode, plansQuery: SipPlansQueryState): boolean {
  return (
    planMode === SipPlanMode.YOUR_PLAN &&
    (plansQuery.isLoading || (plansQuery.isError && plansQuery.isFetching))
  )
}

export function isSipPlansFetchFailed(planMode: SipPlanMode, plansQuery: SipPlansQueryState): boolean {
  return planMode === SipPlanMode.YOUR_PLAN && plansQuery.isError && !plansQuery.isFetching
}

export function isSipIdealPlansLoading(
  planMode: SipPlanMode,
  hasPlansOverview: boolean,
  plansQuery: SipPlansQueryState
): boolean {
  if (planMode !== SipPlanMode.IDEAL_SIPS) {
    return false
  }
  if (plansQuery.isError && plansQuery.isFetching) {
    return true
  }
  return plansQuery.isLoading && !hasPlansOverview
}

export function isSipIdealPlansFetchFailed(planMode: SipPlanMode, plansQuery: SipPlansQueryState): boolean {
  return planMode === SipPlanMode.IDEAL_SIPS && plansQuery.isError && !plansQuery.isFetching
}

export function SipPlansProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams()
  const planTypeQuery = searchParams.get('planType')
  const [planMode, setPlanMode] = useState(
    () => resolvePlanModeFromPlanTypeQuery(planTypeQuery) ?? SipPlanMode.YOUR_PLAN
  )
  const [actionFiltersByPlanMode, setActionFiltersByPlanMode] = useState<
    Record<SipPlanMode, SipActionFilter>
  >(() => createDefaultActionFiltersByPlanMode())
  const [entrySource, setEntrySource] = useState<string | null>(() => searchParams.get('source'))
  const [hasTimerResumeRefetched, setHasTimerResumeRefetched] = useState(false)
  const [hasEditRefetched, setHasEditRefetched] = useState(false)

  useEffect(() => {
    const nextPlanMode = resolvePlanModeFromPlanTypeQuery(planTypeQuery)
    if (nextPlanMode == null) {
      return
    }
    setPlanMode(nextPlanMode)
  }, [planTypeQuery])

  useEffect(() => {
    const sourceFromUrl = searchParams.get('source')
    // In-feature navigations may drop query — only update when URL still carries source
    if (sourceFromUrl != null) {
      setEntrySource(sourceFromUrl)
    }
  }, [searchParams])

  const queryClient = useQueryClient()
  const { showLoading, hideLoading } = useLoading()
  const [isIdealSipEditLoading, setIsIdealSipEditLoading] = useState(false)
  const idealSipEditStartedAtRef = useRef<number | null>(null)
  const idealSipEditLoadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!isIdealSipEditLoading) {
      return
    }

    showLoading()
    idealSipEditStartedAtRef.current = Date.now()

    return () => {
      clearIdealSipEditLoadingTimeout(idealSipEditLoadingTimeoutRef)
      hideLoading()
      idealSipEditStartedAtRef.current = null
    }
  }, [isIdealSipEditLoading, showLoading, hideLoading])

  const actionFilter = actionFiltersByPlanMode[planMode] ?? SipActionFilter.ALL

  const plansQuery = useQuery<SipPlansOverviewResponse>({
    queryKey: [SIP_SUMMARY_QUERY_KEY],
    queryFn: fetchSipPlansOverview,
    retry: SIP_PLANS_API_RETRY_COUNT
  })

  const updateIdealSipAmountMutation = useMutation({
    mutationFn: async (payload: UpdateIdealSipAmountRequest) => {
      await updateIdealSipAmount(payload)
      // Overlay stays up until this returns. Do not throw: a retry would save the amount again.
      try {
        await queryClient.refetchQueries({ queryKey: [SIP_SUMMARY_QUERY_KEY] })
        setHasEditRefetched(true)
      } catch (refetchError) {
        console.error('[sip-plans] ideal SIP summary refetch failed', refetchError)
      }
    },
    retry: SIP_PLANS_API_RETRY_COUNT,
    onSuccess: (_response, { idealSipAmount }) => {
      queryClient.setQueryData<SipPlansOverviewResponse>(
        [SIP_SUMMARY_QUERY_KEY],
        (previousOverview) => {
          if (!previousOverview) {
            return previousOverview
          }
          return {
            ...previousOverview,
            totalIdealSipAmount: idealSipAmount
          }
        }
      )
    },
    onSettled: () => {
      const startedAt = idealSipEditStartedAtRef.current
      const elapsedMs = startedAt == null ? 0 : Date.now() - startedAt
      const remainingMs = Math.max(SIP_EDIT_IDEAL_MIN_LOADING_MS - elapsedMs, 0)

      clearIdealSipEditLoadingTimeout(idealSipEditLoadingTimeoutRef)
      idealSipEditLoadingTimeoutRef.current = setTimeout(() => {
        setIsIdealSipEditLoading(false)
        idealSipEditLoadingTimeoutRef.current = null
      }, remainingMs)
    }
  })

  const derivedPlans = useMemo(
    () => deriveSipPlansView(plansQuery.data),
    [plansQuery.data]
  )

  const planCategoryExpansion = useSipPlanCategoryExpansionState()

  const handlePlanModeChange = (nextPlanMode: SipPlanMode) => {
    lightImpactVibrate()
    setPlanMode(nextPlanMode)
  }

  const handleActionFilterChange = useCallback(
    (nextActionFilter: SipActionFilter) => {
      setActionFiltersByPlanMode((previousFiltersByPlanMode) => ({
        ...previousFiltersByPlanMode,
        [planMode]: nextActionFilter
      }))
    },
    [planMode]
  )

  const submitIdealSipAmount = (totalIdealSipAmount: number) => {
    if (updateIdealSipAmountMutation.isPending || isIdealSipEditLoading) {
      return
    }
    setIsIdealSipEditLoading(true)
    updateIdealSipAmountMutation.mutate({ idealSipAmount: totalIdealSipAmount })
  }

  const plansQueryState = useMemo(
    (): SipPlansQueryState => ({
      isLoading: plansQuery.isLoading,
      isError: plansQuery.isError,
      isFetching: plansQuery.isFetching,
      refetch: () => {
        void plansQuery.refetch()
      }
    }),
    [plansQuery.isLoading, plansQuery.isError, plansQuery.isFetching, plansQuery.refetch]
  )

  const markTimerResumeRefetch = useCallback(() => {
    setHasTimerResumeRefetched(true)
  }, [])

  const leavePlansList = useCallback(() => {
    if (entrySource === 'PORTFOLIO_DASHBOARD' && (hasTimerResumeRefetched || hasEditRefetched)) {
      goToPortfolioDashboard()
      return
    }
    goBack()
  }, [entrySource, hasTimerResumeRefetched, hasEditRefetched])

  const contextValue = useMemo(
    (): SipPlansContextValue => ({
      planMode,
      handlePlanModeChange,
      actionFilter,
      actionFiltersByPlanMode,
      handleActionFilterChange,
      plansOverview: plansQuery.data,
      derivedPlans,
      plansQuery: plansQueryState,
      submitIdealSipAmount,
      ...planCategoryExpansion,
      markTimerResumeRefetch,
      leavePlansList
    }),
    [
      planMode,
      handlePlanModeChange,
      actionFilter,
      actionFiltersByPlanMode,
      handleActionFilterChange,
      plansQuery.data,
      derivedPlans,
      plansQueryState,
      submitIdealSipAmount,
      planCategoryExpansion,
      markTimerResumeRefetch,
      leavePlansList
    ]
  )

  return <SipPlansContext.Provider value={contextValue}>{children}</SipPlansContext.Provider>
}

function createDefaultActionFiltersByPlanMode(): Record<SipPlanMode, SipActionFilter> {
  return {
    [SipPlanMode.YOUR_PLAN]: SipActionFilter.ALL,
    [SipPlanMode.IDEAL_SIPS]: SipActionFilter.ALL
  }
}

function resolvePlanModeFromPlanTypeQuery(planTypeQuery: string | null): SipPlanMode | null {
  if (planTypeQuery === SipPlansPlanType.SIP) {
    return SipPlanMode.YOUR_PLAN
  }
  if (planTypeQuery === SipPlansPlanType.IDEAL) {
    return SipPlanMode.IDEAL_SIPS
  }
  return null
}
